import { useHistory, useParams } from 'react-router';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { putInsurancePolicyPackage } from '@parties/src/features/detail/api/insurance-policies/putInsurancePolicyPackageForParty';
import { putInsurancePolicyForSimulation } from '@flows/src/api/anva-flows/simulation/putInsurancePolicyForSimulation';
import { v4 } from 'uuid';
import cogoToast from 'cogo-toast';
import {
  AnvaQuotesCompletePayload,
  YellowhiveQuotesCompletePayload,
  completeConversation,
} from '@conversations/src/features/detail/shared/api/completeConversation';
import { IdResponse } from '../../../../types/API';
import { InsurancePolicy } from '@advisory-report/src/types/AdvisoryReport';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import useYellowhiveFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useYellowhiveFlowProductsBasketStore';
import useAnvaFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import { isMaatwerkQuote } from '@flows/src/utils/basketCalculations';

const useFinishBasket = (
  sessionId: string,
  setIsCreating: {
    on: () => void;
    off: () => void;
    toggle: () => void;
  },
) => {
  const { flowId } = useParams<{ flowId: string }>();
  const { data: sessionData } = useFlowsSession(sessionId);
  const { data: flowInfo } = useFlowInfo(flowId || '');
  const partyId = sessionData?.prospect?.id || '';
  const history = useHistory();
  // Get YellowHive basket items if this is a YellowHive flow
  const yellowHiveBasketItems = useYellowhiveFlowProductsBasketStore(
    (state) => state.basketItems,
  );

  const { insurances, packages } = useAnvaFlowProductsBasketStore.getState();

  // Combine ANVA insurances with YellowHive basket items for YellowHive flows
  const allNonMaatwerkInsurances =
    flowInfo?.sub_type === 'YELLOWHIVE'
      ? yellowHiveBasketItems.map((item) => ({
          id: item.id,
          name: item.name,
          insurance_company: { name: item.insurance_company_name },
          total_premium: item.total_premium,
          payment_interval: item.payment_interval,
          start_date: item.start_date,
          insured_value: (item as { insured_value?: number | null })
            .insured_value,
          deductible: (item as { deductible?: string | null }).deductible,
          coverages: item.coverages ?? [],
          documents: [], // YellowHive items don't have documents in the same way
          yellowhive_product_id: item.yellowhive_product_id,
          index: 0, // Default index for YellowHive items
          success: true,
          isLoading: false,
          error: null,
          isMaatwerk: false,
          maatwerkRequested: false,
        }))
      : insurances;

  type AnvaCompletion = {
    anva_product_id: string;
    index: number;
    insurance_policy_id: string;
  };
  type YellowhiveCompletion = {
    yellowhive_product_id: string;
    insurance_policy_id: string;
  };
  type CompletionPayload = Array<AnvaCompletion | YellowhiveCompletion>;

  const createInsurancePoliciesForPackages = async (
    _completionPayload: CompletionPayload,
  ): Promise<CompletionPayload> => {
    const completionPayload: CompletionPayload = [..._completionPayload];
    await Promise.all(
      packages.map((pack) => {
        const calc = async () => {
          const createdPackage = await putInsurancePolicyPackage({
            partyId: partyId,
            insurancePackage: {
              ...pack,
              premium: pack.quotes.reduce(
                (acc: number, { total_premium }: { total_premium: number }) =>
                  acc + total_premium,
                0,
              ),
              name: pack.name,
              anva_package_code: pack.code,
            },
          });
          const quotes = await Promise.all(
            pack.quotes.map((quote) =>
              putInsurancePolicyForSimulation({
                partyId: partyId,
                sessionId: sessionId,
                payload: {
                  name: quote.name,
                  insurance_company: quote.insurance_company.name,
                  coverages: quote.coverages
                    .filter(({ selected }) => selected)
                    .map((c) => ({
                      id: v4(),
                      name: c.name,
                      premium: c.total_premium,
                      external_labels: [
                        {
                          label: c.code,
                          type: 'ANVA',
                        },
                      ],
                    })),
                  documents: [],
                  payment_interval: quote.payment_interval ?? undefined,
                  policy_package_id: createdPackage.id,
                  links: quote.documents.map((doc) => ({
                    filename: doc.name,
                    link: doc.link,
                  })),
                  premium: quote.total_premium,
                  anva_product_id: quote.anva_product_id,
                  anva_collection_method_code:
                    quote.anva_collection_method_code ?? undefined,
                  is_tailored_premium: isMaatwerkQuote(quote),
                },
              }),
            ),
          );
          (quotes as IdResponse[]).forEach((quote, i) => {
            const selectedMatched = pack.quotes[i];
            if (selectedMatched && flowInfo?.sub_type === 'ANVA') {
              completionPayload.push({
                anva_product_id: selectedMatched.anva_product_id,
                index: selectedMatched.index,
                insurance_policy_id: quote.id,
              });
            }
          });
        };
        return calc();
      }),
    );
    return completionPayload;
  };

  const createInsurancePoliciesForIndividualQuotes = async (
    _completionPayload: CompletionPayload,
  ): Promise<CompletionPayload> => {
    const completionPayload: CompletionPayload = [..._completionPayload];
    const quotes = await Promise.all(
      allNonMaatwerkInsurances.map(async (quote) => {
        const payload = {
          name: quote.name,
          insurance_company: quote.insurance_company.name,
          // For YellowHive flows, include mandatory coverages as well
          // ANVA continues to include only explicitly selected coverages
          coverages: (
            (quote.coverages ?? []) as Array<{
              selected: boolean;
              mandatory: boolean;
              name: string;
              total_premium?: number;
              code: string;
              insured_value?: number | null;
              deductible?: string | null;
              insured_amount?: number;
            }>
          )
            .filter((coverage) =>
              flowInfo?.sub_type === 'YELLOWHIVE'
                ? coverage.mandatory || coverage.selected
                : coverage.selected,
            )
            .map(
              (c: {
                name: string;
                total_premium?: number;
                code: string;
                insured_value?: number | null;
                deductible?: string | null;
                insured_amount?: number;
              }) => ({
                id: v4(),
                name: c.name,
                premium: c.total_premium ?? 0,
                insured_value:
                  flowInfo?.sub_type === 'YELLOWHIVE'
                    ? c.insured_amount ?? c.insured_value ?? undefined
                    : undefined,
                deductible: c.deductible ?? undefined,
                external_labels: [
                  {
                    label: c.code,
                    type: flowInfo?.sub_type === 'YELLOWHIVE' ? 'ADN' : 'ANVA',
                  },
                ],
              }),
            ),
          documents: [],
          // Always send payment_interval for YellowHive (null if not set);
          // keep ANVA behavior as before
          payment_interval:
            flowInfo?.sub_type === 'YELLOWHIVE'
              ? (quote as { payment_interval?: string | null })
                  .payment_interval ?? null
              : quote.payment_interval ?? undefined,
          // Always include start_date; null when not set for YellowHive
          start_date:
            flowInfo?.sub_type === 'YELLOWHIVE'
              ? (quote as { start_date?: string | null }).start_date ?? null
              : (quote as { start_date?: string | undefined }).start_date ??
                undefined,
          insured_value:
            flowInfo?.sub_type === 'YELLOWHIVE'
              ? (quote as unknown as { insured_value?: number | null })
                  .insured_value ?? undefined
              : undefined,
          deductible:
            flowInfo?.sub_type === 'YELLOWHIVE'
              ? (quote as unknown as { deductible?: string | null })
                  .deductible ?? undefined
              : undefined,
          links: (quote.documents ?? []).map((doc) => ({
            filename: doc.name,
            link: doc.link,
          })),
          premium: quote.total_premium,
          is_tailored_premium: isMaatwerkQuote(quote),
          ...(flowInfo?.sub_type === 'ANVA'
            ? {
                // @ts-expect-error - type mismatch
                anva_product_id: quote.anva_product_id,
                anva_collection_method_code:
                  'anva_collection_method_code' in quote
                    ? quote.anva_collection_method_code ?? undefined
                    : undefined,
              }
            : {
                // @ts-expect-error - type mismatch
                yellowhive_product_id: quote.yellowhive_product_id,
              }),
        };
        const res = await putInsurancePolicyForSimulation({
          partyId: partyId,
          sessionId: sessionId,
          payload: payload as unknown as Partial<InsurancePolicy>,
        });
        return res;
      }),
    );
    (quotes as IdResponse[]).forEach((quote, i) => {
      const selectedMatched = allNonMaatwerkInsurances[i];
      if (!selectedMatched) return;

      if (flowInfo?.sub_type === 'ANVA') {
        completionPayload.push({
          anva_product_id: (
            selectedMatched as unknown as { anva_product_id: string }
          ).anva_product_id,
          index: (selectedMatched as unknown as { index: number }).index,
          insurance_policy_id: quote.id,
        });
      } else {
        completionPayload.push({
          yellowhive_product_id: (
            selectedMatched as unknown as { yellowhive_product_id: string }
          ).yellowhive_product_id,
          insurance_policy_id: quote.id,
        });
      }
    });
    return completionPayload;
  };

  const completeConversationAndNavigate = async (
    completionPayload: CompletionPayload,
  ) => {
    await completeConversation({
      conversationId: sessionId,
      ...(flowInfo?.sub_type === 'ANVA'
        ? {
            anva_quotes: completionPayload as AnvaQuotesCompletePayload,
          }
        : {
            yellowhive_quotes:
              completionPayload as YellowhiveQuotesCompletePayload,
          }),
    });
    history.push({
      pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/selection-overview`,
      search: window.location.search,
    });
  };

  const handleFinish = async () => {
    if (!partyId) return;
    try {
      setIsCreating.on();
      let completionPayload: CompletionPayload = [];
      completionPayload = await createInsurancePoliciesForPackages(
        completionPayload,
      );
      completionPayload = await createInsurancePoliciesForIndividualQuotes(
        completionPayload,
      );
      try {
        await completeConversationAndNavigate(completionPayload);
      } catch (finalizeErr) {
        try {
          const anyErr = finalizeErr as { message?: string };
          const msg = typeof anyErr.message === 'string' ? anyErr.message : '';
          const parsed = msg ? JSON.parse(msg) : null;
          cogoToast.warn(
            parsed?.formatted ||
              parsed?.detail ||
              parsed?.msg ||
              'Could not finalize conversation, continuing anyway',
          );
        } catch {
          cogoToast.warn('Could not finalize conversation, continuing anyway');
        }
        // For YellowHive flows, still proceed to next page if policies were created
        if (flowInfo?.sub_type === 'YELLOWHIVE') {
          history.push({
            pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/selection-overview`,
            search: window.location.search,
          });
        } else {
          throw finalizeErr;
        }
      }
    } catch (err: unknown) {
      try {
        const anyErr = err as { message?: string };
        const msg = typeof anyErr.message === 'string' ? anyErr.message : '';
        const parsed = msg ? JSON.parse(msg) : null;
        cogoToast.error(
          parsed?.formatted ||
            parsed?.detail ||
            parsed?.msg ||
            'Something went wrong',
        );
      } catch {
        cogoToast.error('Something went wrong');
      }
    } finally {
      setIsCreating.off();
    }
  };

  return { handleFinish };
};

export default useFinishBasket;
